import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSettings, useUpdateSettings } from '@/hooks/useDashboardQueries';
import { useAuth } from '@/services/auth/authContext';
import { SettingsConfirmModal, SettingDiffItem } from './SettingsConfirmModal';
import { formatSettingLabel } from './settingsUtils';
import { SettingFieldMeta } from '@/types/api';
import {
  Sliders,
  Shield,
  Clock,
  KeyRound,
  RotateCcw,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Lock,
  Eye,
  EyeOff,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';


type SettingDraftValue = boolean | number | string;
type SettingValues = Record<string, SettingDraftValue>;

export const SettingsView: React.FC = () => {
  const { data: settingsData, isLoading, isError, refetch } = useSettings();
  const updateSettingsMutation = useUpdateSettings();
  const { changePassword } = useAuth();

  // Local draft values (string | number | boolean to preserve user typing & empty field states)
  const [draftValues, setDraftValues] = useState<SettingValues>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [auditReason, setAuditReason] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Change Password Form State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const serverSettings: Record<string, SettingFieldMeta> = useMemo(
    () => settingsData?.settings || {},
    [settingsData]
  );

  // Sync draft state with server values when freshly loaded or when server updates
  useEffect(() => {
    if (settingsData?.settings) {
      const initial: SettingValues = {};
      for (const [k, meta] of Object.entries(settingsData.settings)) {
        if (typeof meta.value === 'boolean' || typeof meta.value === 'number') {
          initial[k] = meta.value;
        }
      }
      setDraftValues(initial);
    }
  }, [settingsData?.settings]);

  // Compute diffs between server state and draft state
  const dirtyDiffs: SettingDiffItem[] = useMemo(() => {
    const diffs: SettingDiffItem[] = [];
    for (const [key, meta] of Object.entries(serverSettings)) {
      const serverVal = meta.value;
      const draftVal = draftValues[key];

      // Unmodified / uninitialized draft values are not dirty
      if (draftVal === undefined) {
        continue;
      }

      if (typeof serverVal === 'boolean') {
        if (typeof draftVal === 'boolean' && draftVal !== serverVal) {
          diffs.push({
            key,
            meta,
            oldValue: serverVal,
            newValue: draftVal,
          });
        }
      } else if (typeof serverVal === 'number') {
        if (draftVal === '' || draftVal === null) {
          // Field was cleared: it differs from persisted server value, but is invalid for submission
          diffs.push({
            key,
            meta,
            oldValue: serverVal,
            newValue: '' as unknown as number,
          });
        } else {
          const parsed = typeof draftVal === 'number' ? draftVal : Number(String(draftVal).trim());
          if (isNaN(parsed)) {
            diffs.push({
              key,
              meta,
              oldValue: serverVal,
              newValue: draftVal as unknown as number,
            });
          } else if (parsed !== serverVal) {
            diffs.push({
              key,
              meta,
              oldValue: serverVal,
              newValue: parsed,
            });
          }
        }
      }
    }
    return diffs;
  }, [serverSettings, draftValues]);

  const hasUnsavedChanges = dirtyDiffs.length > 0;

  // Validate fields against their required and min/max limits
  const validateDraft = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    for (const [key, meta] of Object.entries(serverSettings)) {
      if (meta.type === 'bool') {
        continue;
      }
      const rawVal = draftValues[key];
      const val = rawVal !== undefined ? rawVal : meta.value;
      if (val === '' || val === undefined || val === null) {
        errors[key] = 'Value is required';
        continue;
      }
      const num = typeof val === 'number' ? val : Number(String(val).trim());
      if (isNaN(num)) {
        errors[key] = 'Please enter a valid number';
        continue;
      }
      if (meta.min !== null && meta.min !== undefined && num < meta.min) {
        errors[key] = `Minimum allowed value is ${meta.min}`;
      }
      if (meta.max !== null && meta.max !== undefined && num > meta.max) {
        errors[key] = `Maximum allowed value is ${meta.max}`;
      }
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [serverSettings, draftValues]);

  const handleInputChange = (key: string, value: boolean | number | string) => {
    setDraftValues((prev) => ({
      ...prev,
      [key]: value,
    }));
    // Clear validation error on touch
    if (validationErrors[key]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleResetDraft = () => {
    const initial: SettingValues = {};
    for (const [k, meta] of Object.entries(serverSettings)) {
      if (typeof meta.value === 'boolean' || typeof meta.value === 'number') {
        initial[k] = meta.value;
      }
    }
    setDraftValues(initial);
    setValidationErrors({});
    toast.info('Draft changes discarded.');
  };

  const handleOpenConfirmModal = () => {
    if (!hasUnsavedChanges) {
      return;
    }
    if (!validateDraft()) {
      toast.error('Please fix validation errors before saving.');
      return;
    }
    const hasInvalidDiff = dirtyDiffs.some((d) => {
      if (d.meta.type === 'bool') return typeof d.newValue !== 'boolean';
      const n = Number(d.newValue);
      return isNaN(n) || d.newValue === '' || d.newValue === undefined;
    });
    if (hasInvalidDiff) {
      toast.error('Please fix invalid setting values before applying.');
      return;
    }
    setIsModalOpen(true);
  };

  const handleConfirmSave = async () => {
    const payloadSettings: Record<string, boolean | number> = {};
    for (const diff of dirtyDiffs) {
      if (diff.meta.type === 'bool') {
        payloadSettings[diff.key] = Boolean(diff.newValue);
      } else {
        payloadSettings[diff.key] = Number(diff.newValue);
      }
    }

    try {
      await updateSettingsMutation.mutateAsync({
        settings: payloadSettings,
        reason: auditReason.trim() ? auditReason.trim() : undefined,
      });
      setIsModalOpen(false);
      setAuditReason('');
      toast.success('Runtime settings applied successfully.');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to update settings.';
      toast.error(msg);
    }
  };

  const handleChangePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);

    if (!currentPassword) {
      setPasswordError('Current password is required.');
      return;
    }
    if (!newPassword || newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }

    setIsChangingPassword(true);
    try {
      await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to change password.';
      setPasswordError(msg);
    } finally {
      setIsChangingPassword(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 space-y-4">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
        <p className="font-mono text-xs text-slate-400">Loading runtime settings...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6 rounded-xl bg-rose-950/30 border border-rose-800/50 text-rose-300 font-mono text-xs space-y-3">
        <div className="flex items-center gap-2 font-bold text-sm">
          <AlertTriangle className="w-5 h-5 text-rose-400" />
          <span>Failed to load engine settings</span>
        </div>
        <p>The backend settings endpoint could not be reached or returned an error.</p>
        <button
          onClick={() => refetch()}
          className="px-3 py-1.5 rounded bg-rose-900/60 hover:bg-rose-900 border border-rose-700 text-white transition-all text-xs font-semibold"
        >
          Retry Fetch
        </button>
      </div>
    );
  }

  // Filter settings by category
  const tradingSettings = Object.entries(serverSettings).filter(
    ([, meta]) => meta.category === 'trading'
  );
  const riskSettings = Object.entries(serverSettings).filter(
    ([, meta]) => meta.category === 'risk'
  );
  const exitSettings = Object.entries(serverSettings).filter(
    ([, meta]) => meta.category === 'exit'
  );

  return (
    <div className="space-y-6 pb-28 sm:pb-32 font-sans">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[var(--border-default)] pb-4">
        <div>
          <h1 className="text-xl font-bold font-mono text-[var(--text-primary)] flex items-center gap-2">
            <Sliders className="w-5 h-5 text-emerald-400" />
            <span>Runtime Engine Configuration</span>
          </h1>
          <p className="text-xs font-mono text-[var(--text-muted)] mt-1">
            Configure authoritative risk limits, sizing, and exit rules for the active trading bot.
          </p>
        </div>
        {settingsData?.updated_at && (
          <div className="text-right text-[11px] font-mono text-[var(--text-muted)]">
            <div>Last Updated:</div>
            <div className="text-[var(--text-secondary)]">{new Date(settingsData.updated_at).toLocaleString()}</div>
          </div>
        )}
      </div>

      {/* Grid of 3 Core Categories */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Card 1: Trading Controls */}
        <div className="bg-[var(--bg-surface)] border border-[var(--border-default)] rounded-xl p-4 sm:p-5 space-y-4 sm:space-y-5 flex flex-col justify-between">
          <div className="space-y-4">
            <div className="flex items-center gap-2.5 text-[var(--text-primary)] border-b border-[var(--border-default)] pb-3">
              <div className="p-2 rounded-lg bg-emerald-950/60 border border-emerald-800/60 text-emerald-400">
                <Sliders className="w-4 h-4" />
              </div>
              <div>
                <h2 className="font-mono font-bold text-sm">Trading Controls</h2>
                <p className="text-[11px] font-mono text-[var(--text-muted)]">Core execution switches & capital sizing</p>
              </div>
            </div>

            <div className="space-y-4">
              {tradingSettings.map(([key, meta]) => (
                <SettingFieldInput
                  key={key}
                  settingKey={key}
                  meta={meta}
                  value={draftValues[key]}
                  serverValue={typeof meta.value === 'boolean' || typeof meta.value === 'number' ? meta.value : undefined}
                  error={validationErrors[key]}
                  onChange={(val) => handleInputChange(key, val)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Card 2: Risk Management */}
        <div className="bg-[var(--bg-surface)] border border-[var(--border-default)] rounded-xl p-4 sm:p-5 space-y-4 sm:space-y-5 flex flex-col justify-between">
          <div className="space-y-4">
            <div className="flex items-center gap-2.5 text-[var(--text-primary)] border-b border-[var(--border-default)] pb-3">
              <div className="p-2 rounded-lg bg-amber-950/60 border border-amber-800/60 text-amber-400">
                <Shield className="w-4 h-4" />
              </div>
              <div>
                <h2 className="font-mono font-bold text-sm">Risk Limits</h2>
                <p className="text-[11px] font-mono text-[var(--text-muted)]">Daily drawdown & stop targets</p>
              </div>
            </div>

            <div className="space-y-4">
              {riskSettings.map(([key, meta]) => (
                <SettingFieldInput
                  key={key}
                  settingKey={key}
                  meta={meta}
                  value={draftValues[key]}
                  serverValue={typeof meta.value === 'boolean' || typeof meta.value === 'number' ? meta.value : undefined}
                  error={validationErrors[key]}
                  onChange={(val) => handleInputChange(key, val)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Card 3: Exit Rules */}
        <div className="bg-[var(--bg-surface)] border border-[var(--border-default)] rounded-xl p-4 sm:p-5 space-y-4 sm:space-y-5 flex flex-col justify-between">
          <div className="space-y-4">
            <div className="flex items-center gap-2.5 text-[var(--text-primary)] border-b border-[var(--border-default)] pb-3">
              <div className="p-2 rounded-lg bg-cyan-950/60 border border-cyan-800/60 text-cyan-400">
                <Clock className="w-4 h-4" />
              </div>
              <div>
                <h2 className="font-mono font-bold text-sm">Exit Strategy</h2>
                <p className="text-[11px] font-mono text-[var(--text-muted)]">Time-based position squaring</p>
              </div>
            </div>

            <div className="space-y-4">
              {exitSettings.map(([key, meta]) => (
                <SettingFieldInput
                  key={key}
                  settingKey={key}
                  meta={meta}
                  value={draftValues[key]}
                  serverValue={typeof meta.value === 'boolean' || typeof meta.value === 'number' ? meta.value : undefined}
                  error={validationErrors[key]}
                  onChange={(val) => handleInputChange(key, val)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Security & Password Card */}
      <div className="bg-[var(--bg-surface)] border border-[var(--border-default)] rounded-xl p-4 sm:p-5 space-y-5">
        <div className="flex items-center gap-2.5 text-[var(--text-primary)] border-b border-[var(--border-default)] pb-3">
          <div className="p-2 rounded-lg bg-purple-950/60 border border-purple-800/60 text-purple-400">
            <KeyRound className="w-4 h-4" />
          </div>
          <div>
            <h2 className="font-mono font-bold text-sm">Operator Credentials</h2>
            <p className="text-[11px] font-mono text-[var(--text-muted)]">
              Update password and rotate active sessions (Requires re-login)
            </p>
          </div>
        </div>


        <form onSubmit={handleChangePasswordSubmit} className="max-w-xl space-y-4">
          {passwordError && (
            <div className="p-3 rounded-lg bg-rose-950/40 border border-rose-800/60 text-rose-300 text-xs font-mono flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-rose-400 shrink-0" />
              <span>{passwordError}</span>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="block text-xs font-mono text-slate-300">Current Password</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500">
                <Lock className="w-4 h-4" />
              </div>
              <input
                type={showCurrentPw ? 'text' : 'password'}
                autoComplete="current-password"
                disabled={isChangingPassword}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className="w-full pl-9 pr-10 py-2 bg-slate-950 border border-slate-800 rounded-md text-xs font-mono text-slate-100 focus:ring-1 focus:ring-purple-500 focus:outline-none"
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowCurrentPw(!showCurrentPw)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-slate-300"
              >
                {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="block text-xs font-mono text-slate-300">New Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500">
                  <Lock className="w-4 h-4" />
                </div>
                <input
                  type={showNewPw ? 'text' : 'password'}
                  autoComplete="new-password"
                  disabled={isChangingPassword}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min 8 characters"
                  className="w-full pl-9 pr-10 py-2 bg-slate-950 border border-slate-800 rounded-md text-xs font-mono text-slate-100 focus:ring-1 focus:ring-purple-500 focus:outline-none"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowNewPw(!showNewPw)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-slate-300"
                >
                  {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-xs font-mono text-slate-300">Confirm New Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500">
                  <Lock className="w-4 h-4" />
                </div>
                <input
                  type={showNewPw ? 'text' : 'password'}
                  autoComplete="new-password"
                  disabled={isChangingPassword}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  className="w-full pl-9 pr-3 py-2 bg-slate-950 border border-slate-800 rounded-md text-xs font-mono text-slate-100 focus:ring-1 focus:ring-purple-500 focus:outline-none"
                />
              </div>
            </div>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={isChangingPassword || !currentPassword || !newPassword || !confirmPassword}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-purple-600 hover:bg-purple-500 active:bg-purple-700 disabled:bg-slate-800 disabled:text-slate-500 text-white font-mono text-xs font-semibold transition-all shadow-md shadow-purple-950/50 cursor-pointer"
            >
              {isChangingPassword ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Updating Password...</span>
                </>
              ) : (
                <>
                  <KeyRound className="w-4 h-4" />
                  <span>Update Password & Revoke Sessions</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Always-reachable Sticky Action Bar */}
      <div className="sticky bottom-0 sm:bottom-2 z-30 pt-4 pb-2">
        <div
          className={`w-full max-w-3xl mx-auto rounded-xl p-3 sm:p-4 border backdrop-blur-md shadow-2xl transition-all duration-200 flex flex-col sm:flex-row items-center justify-between gap-3 font-mono text-xs ${
            hasUnsavedChanges
              ? 'bg-[var(--bg-surface)]/95 border-emerald-500/60 shadow-emerald-950/40 ring-1 ring-emerald-500/30'
              : 'bg-[var(--bg-surface)]/85 border-[var(--border-default)] text-[var(--text-muted)]'
          }`}
        >
          <div className="flex items-center gap-2.5 w-full sm:w-auto justify-between sm:justify-start">
            <div className="flex items-center gap-2">
              <span
                className={`w-2.5 h-2.5 rounded-full ${
                  hasUnsavedChanges ? 'bg-emerald-400 animate-pulse' : 'bg-[var(--text-dim)]'
                }`}
              />
              <span className="font-semibold text-[var(--text-primary)]">
                {hasUnsavedChanges ? (
                  <span>
                    {dirtyDiffs.length} unsaved setting modification{dirtyDiffs.length > 1 ? 's' : ''}
                  </span>
                ) : (
                  <span className="text-[var(--text-muted)]">Settings synchronized with engine</span>
                )}
              </span>
            </div>
            {hasUnsavedChanges && (
              <span className="text-[10px] text-amber-400 font-normal sm:hidden">
                Draft active
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            {hasUnsavedChanges && (
              <button
                type="button"
                onClick={handleResetDraft}
                className="flex-1 sm:flex-initial flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-[var(--border-strong)] bg-[var(--bg-surface-elevated)] hover:bg-[var(--bg-surface-muted)] text-[var(--text-secondary)] font-semibold transition-all active:scale-95 cursor-pointer text-xs"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Discard</span>
              </button>
            )}
            <button
              type="button"
              disabled={!hasUnsavedChanges || updateSettingsMutation.isPending}
              onClick={handleOpenConfirmModal}
              className={`flex-1 sm:flex-initial flex items-center justify-center gap-1.5 px-4 py-2 rounded-md font-semibold text-xs transition-all active:scale-95 cursor-pointer ${
                hasUnsavedChanges
                  ? 'bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white shadow-lg shadow-emerald-950/50'
                  : 'bg-[var(--bg-surface-elevated)]/80 text-[var(--text-dim)] border border-[var(--border-default)]/50 cursor-not-allowed opacity-60'
              }`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>Review & Apply</span>
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <SettingsConfirmModal
        isOpen={isModalOpen}
        diffs={dirtyDiffs}
        reason={auditReason}
        onReasonChange={setAuditReason}
        onConfirm={handleConfirmSave}
        onCancel={() => setIsModalOpen(false)}
        isSaving={updateSettingsMutation.isPending}
      />
    </div>
  );
};

interface SettingFieldInputProps {
  settingKey: string;
  meta: SettingFieldMeta;
  value: boolean | number | string | undefined;
  serverValue: boolean | number | undefined;
  error?: string;
  onChange: (value: boolean | number | string) => void;
}

const SettingFieldInput: React.FC<SettingFieldInputProps> = ({
  settingKey,
  meta,
  value,
  serverValue,
  error,
  onChange,
}) => {
  const effectiveValue = value !== undefined ? value : serverValue;

  const isDirty = useMemo(() => {
    if (value === undefined || serverValue === undefined) return false;
    if (meta.type === 'bool') {
      return Boolean(value) !== Boolean(serverValue);
    }
    if (value === '') return true;
    const num = typeof value === 'number' ? value : Number(String(value).trim());
    if (isNaN(num)) return true;
    return num !== serverValue;
  }, [value, serverValue, meta.type]);

  if (meta.type === 'bool') {
    const isChecked = Boolean(effectiveValue);
    return (
      <div className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-[var(--bg-surface-elevated)]/50 border border-[var(--border-default)]">
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs font-semibold text-[var(--text-primary)]">
              {formatSettingLabel(settingKey, meta)}
            </span>
            {meta.affects_existing_positions && (
              <span className="px-1.5 py-0.2 rounded text-[9px] font-mono bg-amber-950/80 border border-amber-800/60 text-amber-300">
                Active Pos
              </span>
            )}
            {isDirty && (
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" title="Modified in draft" />
            )}
          </div>
          {meta.description && (
            <p className="text-[10px] font-mono text-[var(--text-muted)]">{meta.description}</p>
          )}
        </div>

        <button
          type="button"
          role="switch"
          aria-checked={isChecked}
          onClick={() => onChange(!isChecked)}
          className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500/50 ${
            isChecked ? 'bg-emerald-600' : 'bg-[var(--bg-surface-muted)]'
          }`}
        >
          <span
            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              isChecked ? 'translate-x-4' : 'translate-x-0'
            }`}
          />
        </button>
      </div>
    );
  }

  const displayValue =
    effectiveValue === undefined || effectiveValue === null ? '' : String(effectiveValue);

  return (
    <div className="space-y-1.5 p-2.5 rounded-lg bg-[var(--bg-surface-elevated)]/50 border border-[var(--border-default)]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <label htmlFor={`input-${settingKey}`} className="font-mono text-xs font-semibold text-[var(--text-primary)]">
            {formatSettingLabel(settingKey, meta)}
          </label>
          {meta.affects_existing_positions && (
            <span className="px-1.5 py-0.2 rounded text-[9px] font-mono bg-amber-950/80 border border-amber-800/60 text-amber-300">
              Active Pos
            </span>
          )}
          {isDirty && (
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" title="Modified in draft" />
          )}
        </div>
        {meta.unit && (
          <span className="text-[10px] font-mono text-[var(--text-dim)] uppercase">
            {meta.unit}
          </span>
        )}
      </div>

      {meta.description && (
        <p className="text-[10px] font-mono text-[var(--text-muted)]">{meta.description}</p>
      )}

      <div className="flex items-center gap-2">
        <input
          id={`input-${settingKey}`}
          type="number"
          min={meta.min ?? undefined}
          max={meta.max ?? undefined}
          step={meta.type === 'float' ? '0.25' : '1'}
          value={displayValue}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full px-3 py-1.5 bg-[var(--bg-input)] border rounded-md text-xs font-mono text-[var(--text-primary)] focus:outline-none focus:ring-1 ${
            error
              ? 'border-rose-500 focus:ring-rose-500'
              : 'border-[var(--border-default)] focus:ring-[var(--focus-ring)]'
          }`}
        />
      </div>

      <div className="flex items-center justify-between text-[10px] font-mono text-[var(--text-dim)]">
        <span>
          Range: {meta.min ?? '—'} - {meta.max ?? '—'}
        </span>
        {isDirty && serverValue !== undefined && (
          <span className="text-[var(--text-muted)]">Live: {serverValue}</span>
        )}
      </div>

      {error && (
        <p className="text-[10px] font-mono text-rose-400 font-semibold">{error}</p>
      )}
    </div>
  );
};
